import { mergeLeft, omit } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { warn } from '../environment'
import { getLocalStorageItem } from '../storage'
import { isFunction } from '../type'

type SetValue<T> = (value: T | ((prev: T) => T), stringify?: boolean) => void

export function useLocalStorage<T = unknown>(
  key: string,
  initialValue: T,
): [T, SetValue<T>] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = getLocalStorageItem(key)
      return item ?? initialValue
    } catch (_error) {
      return initialValue
    }
  })

  const setValue: SetValue<T> = (value, stringify = true) => {
    try {
      const valueToStore = isFunction(value) ? value(storedValue) : value

      setStoredValue(valueToStore)
      localStorage.setItem(
        key,
        stringify ? JSON.stringify(valueToStore) : (valueToStore as string),
      )
    } catch (error) {
      warn(error)
    }
  }

  return [storedValue, setValue]
}

type UseLocalStorageObjectParams<T> = {
  parent: string
  key: string
  initialValue: T
}

export const useLocalStorageObject = <T = unknown>({
  parent,
  key,
  initialValue,
}: UseLocalStorageObjectParams<T>) => {
  const [object, setObject] = useLocalStorage<Record<string, T>>(parent, {})

  const value = useMemo<T>(() => object[key] ?? initialValue, [
    key,
    object,
    initialValue,
  ])

  const setValue = useCallback(
    (newValue: T) => {
      setObject(mergeLeft({ [key]: newValue }))
    },
    [key, setObject],
  )

  const clearValue = useCallback(() => {
    setObject(omit([key]))
  }, [key, setObject])

  return {
    value,
    setValue,
    clearValue,
  }
}
